import { useEffect, useState, useMemo } from "react";
import { TeamResult } from "@/types";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Spinner, Pagination } from "@heroui/react";
import { Link } from 'react-router-dom';
import TeamLogo from "../team-logo";
import { isMobile } from "@/stores/MenuStore";
import { Icon } from "@iconify/react";
import { Tooltip } from "@heroui/tooltip";
import { useTranslation } from 'react-i18next';
import { formatTimestampToLocalTime } from "@/utils/time";

interface FinderTeamResultProps {
    tableResult: TeamResult[];
    leaguesNames: string[] | undefined;
    statName: string | undefined;
    atLeast: string;
    lastGames: string;
    hitRate: number;
    seeResult: boolean;
    loading: boolean;
}

const formatNumber = (value: any): string => {
    if (typeof value === 'number') {
        return value % 1 === 0 ? value.toString() : value.toFixed(1);
    }
    return value;
};

const FinderTeamResult = function FinderTeamResult({ tableResult, leaguesNames, statName, atLeast, lastGames, hitRate, seeResult, loading }: FinderTeamResultProps) {
    const { t } = useTranslation();
    const columns = [
        { key: "team_name", label: t("finder_team.col_team") },
        { key: "next_match", label: t("finder_team.col_next_match") },
        { key: "datetime", label: t("finder_team.col_datetime") },
        { key: "total", label: t("finder_team.col_total") },
        { key: "average", label: t("finder_team.col_average") },
        { key: "hit_rate", label: t("finder_team.col_hit_rate") },
        { key: "form", label: t("finder_result.finder_result_7") },
    ];

    const [selectedSort, setSelectedSort] = useState<keyof TeamResult>('total');
    const [descSort, setDescSort] = useState<boolean>(true);
    const [tableSort, setTableSort] = useState<TeamResult[]>([]);

    const [page, setPage] = useState(1);
    const rowsPerPage = isMobile() ? 11 : 15;
    const totalPages = Math.ceil(tableSort.length / rowsPerPage);

    const cellResultsByKey = (team: TeamResult, key: string | number) => {
        if (key == 'team_name') return (
            <Link to={`/team/${team.team_id}`}>
                <div className="flex items-center">
                    <TeamLogo className='ms-1 me-2' teamId={team.team_id} size={24} />
                    <div className="flex flex-col">
                        <span className={`${isMobile() ? 'text-xs' : 'text-sm'} text-default-800 flex pl-1 font-medium`}>{team.team_name}</span>
                    </div>
                </div>
            </Link>
        )
        if (key == 'league_name') return (
            <span className={`${isMobile() ? 'text-xs' : 'text-sm'} text-default-600`}>{team.league_name}</span>
        )
        if (key == 'next_match') return (
            <Link className="flex items-center justify-center gap-1" to={`/game/${team.next_fixture_id}`}>
                <TeamLogo className='mx-1' teamId={team.opponent_id} size={24} />
                <span className={`${isMobile() ? 'text-xs' : 'text-sm'} text-default-700`}>{team.opponent_name}</span>
            </Link>
        )
        if (key == 'datetime') return (
            <div className="flex flex-col items-center">
                <span className={`${isMobile() ? 'text-xs' : 'text-sm'} text-default-700`}>{formatTimestampToLocalTime(Number(team.next_fixture_date))}</span>
            </div>
        )
        if (key == 'hit_rate') return (
            <span className={`text-default-700 font-semibold`}>{`${Number(team[key]) * 100}%`}</span>
        )
        if (key == 'form') return (
            <div className="flex">
                {(team.form ?? []).map((stat: number, i: number) => (
                    <span key={`${team.team_id}-form-${i}`} style={{ fontFamily: '"Inter", sans-serif, "Courier", "Courier New", monospace' }} className={`font-semibold pr-1 text-xs ${Number(stat) >= Number(atLeast) ? 'text-green-600' : 'text-red-500'}`}>
                        {`${Number(stat)}`}
                    </span>
                ))}
            </div>
        )
        return <span className={`text-default-700 font-semibold`}>{formatNumber(team[key as keyof TeamResult])}</span>;
    }

    const filterStatsClass = 'flex rounded-large text-xs font-normal text-default-600 border-2 border-primary-500 px-2 py-[6px]';
    const isSortColumn = (columnKey: string) => ['team_name', 'total', 'average', 'hit_rate'].includes(columnKey);

    const setSorted = (sortKey: any) => {
        if (isSortColumn(sortKey)) {
            if (sortKey == selectedSort) setDescSort(!descSort);
            else setSelectedSort(sortKey);
        }
    }

    useEffect(() => {
        const sortedTable = [...tableResult].sort((a, b) => {
            if (selectedSort === "team_name") {
                return descSort
                    ? a.team_name.localeCompare(b.team_name)
                    : b.team_name.localeCompare(a.team_name);
            } else {
                const valueA = a[selectedSort] as number;
                const valueB = b[selectedSort] as number;
                return descSort ? valueB - valueA : valueA - valueB;
            }
        });
        setTableSort(sortedTable);
    }, [selectedSort, descSort, tableResult]);

    const paginatedTeams = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return tableSort.slice(start, start + rowsPerPage);
    }, [page, tableSort]);

    return (
        <div className="flex w-full flex-col pb-5 gap-4 rounded-large">
            {loading ? <div className="flex w-full h-full items-center justify-center"><Spinner /></div> : (tableResult.length ?
                <div className="flex flex-col gap-2">
                    <div className="flex gap-2 flex-wrap items-center">
                        <span className={filterStatsClass}>{`${atLeast}+ ${statName}`}</span>
                        <span className={filterStatsClass}>{t('finder_result.finder_result_8', { lastGames })}</span>
                        <span className={filterStatsClass}>{t('finder_result.finder_result_9', { hitRate: hitRate * 100 })}</span>
                        {leaguesNames && (
                            <Tooltip content={leaguesNames.map((l, i) => `${l}${leaguesNames.length - 1 == i ? '.' : ', '}`)}>
                                <span className={filterStatsClass}>
                                    {leaguesNames.length > 4
                                        ? t('finder_result.finder_result_10', { leagues: leaguesNames.slice(0, 4).join(', '), more: leaguesNames.length - 4 })
                                        : leaguesNames.map(l => l).join(', ')
                                    }
                                </span>
                            </Tooltip>
                        )}
                    </div>
                    <Table
                        className="fadeIn"
                        classNames={{ wrapper: `p-0 rounded-medium shadow-large max-w-[1040px] overflow-y-hidden` }}
                        aria-label="table-team-result"
                        bottomContent={
                            <div className="flex w-full justify-center mt-3 mb-7">
                                <Pagination
                                    showControls
                                    total={totalPages}
                                    page={page}
                                    onChange={setPage}
                                />
                            </div>
                        }
                    >
                        <TableHeader columns={columns}>
                            {(column) => <TableColumn align={['team_name'].includes(column.key) ? 'start' : 'center'} key={column.key}>
                                <div onClick={() => setSorted(column.key)} className={`flex items-center w-full gap-2 hover:text-default-700 ${['next_match', 'total', 'average', 'hit_rate', 'league_name', 'datetime'].includes(column.key) && 'justify-center'} ${selectedSort == column.key && 'text-default-900'} ${isSortColumn(column.key) && 'cursor-pointer'}`}>
                                    <span>{column.label}</span>
                                    {isSortColumn(column.key) && <Icon className={`text-lg transition ${selectedSort == column.key && !descSort && 'rotate-180'}`} icon="material-symbols:keyboard-arrow-down" />}
                                </div>
                            </TableColumn>}
                        </TableHeader>
                        <TableBody>
                            {paginatedTeams.map((item: TeamResult, i) => (
                                <TableRow className={`hover:bg-default-200 transition ${i % 2 != 0 && 'bg-default-100'}`} key={`${item.team_id}-${item.next_fixture_id}`}>
                                    {(columnKey) => <TableCell>{cellResultsByKey(item, columnKey)}</TableCell>}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                : (seeResult && <div className="flex w-full h-full gap-2 items-center justify-center fadeIn">
                    <Icon className="text-3xl text-default-400" icon="material-symbols:speaker-notes-off-outline" />
                    <span className="text-lg text-default-500">{t('finder_result.finder_result_11')}</span>
                </div>)
            )}
        </div>
    );
};

export default FinderTeamResult;
